/**
 * ResponsiveFilters - a filter row that folds into a "Filters" pop-up on
 * smaller screens.
 *
 * Atomic Design role: Organism (it composes the Modal organism).
 *
 * Why: on a phone or an iPad (517px of content once the sidebar takes its
 * share) a row of dropdowns, chips and dates wraps into three or four lines
 * and shoves the table off the screen. Below the `desktop:` breakpoint this
 * component renders a single FilterButton instead; tapping it opens a Modal
 * holding the very same controls, stacked full width. Desktop is unchanged —
 * the controls render inline exactly where the page puts them.
 *
 * The controls are rendered ONCE — inline or in the pop-up, never both (see
 * useMediaQuery for why CSS show/hide isn't good enough for live inputs).
 * Filters still apply as they change, like they always have; "Show results"
 * just closes the pop-up so the list is visible again.
 *
 * Keep search boxes OUT of `children` — searching is the first thing people
 * reach for, so it stays on screen at every size. Group related chips in one
 * wrapper element, or the stacked pop-up gives each chip its own full row.
 *
 * @example
 * <ResponsiveFilters activeCount={count} onReset={clearFilters} className="flex flex-wrap gap-8px">
 *   <select …/>
 *   <select …/>
 * </ResponsiveFilters>
 */
import React, { useState } from 'react';
import { FilterButton } from '../../atoms/FilterButton';
import { Button } from '../../atoms/Button';
import { Modal } from '../Modal';
import { useIsDesktop } from '../../../hooks/useMediaQuery';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface ResponsiveFiltersProps {
	/** The filter controls — selects, chip groups, date pickers. No search box. */
	children: React.ReactNode;
	/** How many filters differ from their defaults; shown on the button. */
	activeCount?: number;
	/** Puts every filter back to its default. Shows a Reset button in the pop-up when given. */
	onReset?: () => void;
	/** Pop-up heading — name what's being filtered when it helps ("Filter tasks"). */
	title?: string;
	/** Classes for the inline (desktop) wrapper — the row's own layout. */
	className?: string;
	/** Classes for the collapsed wrapper around the Filters button (e.g. alignment). */
	triggerClassName?: string;
	/** Overrides the organism's data-ref so a composing component keeps its identity. */
	dataRef?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const ResponsiveFilters: React.FC<ResponsiveFiltersProps> = ({
	children,
	activeCount = 0,
	onReset,
	title = 'Filters',
	className = '',
	triggerClassName = '',
	dataRef = 'organism-responsive-filters',
}) => {
	const isDesktop = useIsDesktop();
	const [isOpen, setIsOpen] = useState(false);

	/* Desktop: render the controls in place, untouched. Growing the window
	   past the breakpoint also unmounts the pop-up, so it can't linger. */
	if (isDesktop) {
		return (
			<div className={`responsive-filters responsive-filters--inline ${className}`.trim()} data-ref={dataRef}>
				{children}
			</div>
		);
	}

	return (
		<div className={`responsive-filters responsive-filters--collapsed ${triggerClassName}`.trim()} data-ref={dataRef}>
			<FilterButton activeCount={activeCount} onClick={() => setIsOpen(true)} aria-haspopup="dialog" dataRef={`${dataRef}-button`} />
			<Modal
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				title={title}
				size="md"
				className="responsive-filters__modal"
				dataRef={`${dataRef}-modal`}
				/* Pinned under the scrolling controls; full-width thumb targets on phones. */
				footer={(
					<div className="responsive-filters__actions flex flex-col-reverse tablet:flex-row tablet:justify-end gap-8px">
						{onReset && (
							<Button type="button" variant="secondary" size="sm" onClick={onReset} disabled={activeCount === 0} className="w-full tablet:w-auto">
								Reset
							</Button>
						)}
						<Button type="button" variant="primary" size="sm" onClick={() => setIsOpen(false)} className="w-full tablet:w-auto">
							Show results
						</Button>
					</div>
				)}
			>
				{/* Stack every control full width — inline rows are sized for a
				    wide toolbar, not a narrow pop-up. The FilterBar molecule lays
				    its own dropdowns out in a wrapping row, so stack that too. */}
				<div className="responsive-filters__stack flex flex-col gap-16px [&>*]:w-full [&>*]:max-w-none [&_.filter-bar]:flex-col [&_.filter-bar]:items-stretch">
					{children}
				</div>
			</Modal>
		</div>
	);
};

export default ResponsiveFilters;
